/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetStripeCheckoutStatusRequest,
  GetStripeCheckoutStatusSecurity,
} from "../models/operations/getstripecheckoutstatus.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildGetStripeCheckoutStatusQuery,
  GetStripeCheckoutStatusQueryData,
  prefetchGetStripeCheckoutStatus,
  queryKeyGetStripeCheckoutStatus,
} from "./getStripeCheckoutStatus.core.js";
export {
  buildGetStripeCheckoutStatusQuery,
  type GetStripeCheckoutStatusQueryData,
  prefetchGetStripeCheckoutStatus,
  queryKeyGetStripeCheckoutStatus,
};

export type GetStripeCheckoutStatusQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getStripeCheckoutStatus usage
 *
 * @remarks
 * Read the current status of a Stripe Checkout session owned by the active organization
 */
export function useGetStripeCheckoutStatus(
  request: GetStripeCheckoutStatusRequest,
  security?: GetStripeCheckoutStatusSecurity | undefined,
  options?: QueryHookOptions<
    GetStripeCheckoutStatusQueryData,
    GetStripeCheckoutStatusQueryError
  >,
): UseQueryResult<
  GetStripeCheckoutStatusQueryData,
  GetStripeCheckoutStatusQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildGetStripeCheckoutStatusQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getStripeCheckoutStatus usage
 *
 * @remarks
 * Read the current status of a Stripe Checkout session owned by the active organization
 */
export function useGetStripeCheckoutStatusSuspense(
  request: GetStripeCheckoutStatusRequest,
  security?: GetStripeCheckoutStatusSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    GetStripeCheckoutStatusQueryData,
    GetStripeCheckoutStatusQueryError
  >,
): UseSuspenseQueryResult<
  GetStripeCheckoutStatusQueryData,
  GetStripeCheckoutStatusQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildGetStripeCheckoutStatusQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setGetStripeCheckoutStatusData(
  client: QueryClient,
  queryKeyBase: [
    parameters: { sessionId: string; gramSession?: string | undefined },
  ],
  data: GetStripeCheckoutStatusQueryData,
): GetStripeCheckoutStatusQueryData | undefined {
  const key = queryKeyGetStripeCheckoutStatus(...queryKeyBase);

  return client.setQueryData<GetStripeCheckoutStatusQueryData>(key, data);
}

export function invalidateGetStripeCheckoutStatus(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { sessionId: string; gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "usage",
      "getStripeCheckoutStatus",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllGetStripeCheckoutStatus(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "usage", "getStripeCheckoutStatus"],
  });
}
